/**
 * 18+ identity verification helpers.
 *
 * Status lives on the Sharetribe user. publicData is used for the Verified
 * badge and for checking the other party. protectedData repeats the flags
 * for the owner. ID images and raw PII are never stored.
 */

export const IDENTITY_REQUIRED_CODE = 'identity_required';
export const IDENTITY_STANDIN_COMPLETE_SEEN_KEY = 'identityStandinCompleteSeen';

/** localStorage cache. Source of truth is protectedData on the user. */
export const identityStandinCompleteSeenKey = userId =>
  `sl-identity-standin-complete-seen:${userId}`;

const shownStandinCompleteThisLoad = new Set();

const userIdOf = userOrId => {
  if (!userOrId) {
    return null;
  }
  if (typeof userOrId === 'string') {
    return userOrId;
  }
  return userOrId.id?.uuid || userOrId.uuid || null;
};

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const profileOf = user => user?.attributes?.profile || {};

/**
 * True when Sharetribe already stored that this user saw the complete card.
 *
 * @param {Object} [user]
 * @returns {boolean}
 */
export const hasIdentityStandinCompleteSeenOnUser = user => {
  if (!user || typeof user !== 'object') {
    return false;
  }
  const publicData = profileOf(user).publicData || {};
  const protectedData = profileOf(user).protectedData || {};
  return !!(
    protectedData[IDENTITY_STANDIN_COMPLETE_SEEN_KEY] ||
    publicData[IDENTITY_STANDIN_COMPLETE_SEEN_KEY]
  );
};

/**
 * True when this user already dismissed or viewed the TEST stand-in complete card.
 * Server-side protectedData wins; localStorage is only a same-browser cache.
 *
 * @param {string|Object} userOrId
 * @returns {boolean}
 */
export const hasSeenIdentityStandinComplete = userOrId => {
  if (hasIdentityStandinCompleteSeenOnUser(userOrId)) {
    return true;
  }
  const userId = userIdOf(userOrId);
  if (!userId || !canUseStorage()) {
    return false;
  }
  try {
    return window.localStorage.getItem(identityStandinCompleteSeenKey(userId)) === '1';
  } catch (e) {
    return false;
  }
};

/**
 * Persist that this user has seen the TEST stand-in complete card.
 * Also remembered in-memory so a same-page remount (after verify) can still show it once.
 *
 * @param {string|Object} userOrId
 */
export const markIdentityStandinCompleteSeen = userOrId => {
  const userId = userIdOf(userOrId);
  if (!userId) {
    return;
  }
  shownStandinCompleteThisLoad.add(userId);
  if (!canUseStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(identityStandinCompleteSeenKey(userId), '1');
  } catch (e) {
    // Private mode / quota — in-memory flag still covers this page load.
  }
};

/**
 * Show the complete card only immediately after the stand-in on this page load.
 * A verified user landing on another page (or a new device) must not see it.
 *
 * @param {string|Object} userOrId
 * @returns {boolean}
 */
export const shouldShowIdentityStandinComplete = userOrId => {
  const userId = userIdOf(userOrId);
  if (!userId) {
    return false;
  }
  return shownStandinCompleteThisLoad.has(userId);
};

/**
 * Dashboard toast: only with ?standin=done, and only the first time.
 *
 * @param {string} [search]
 * @param {string|Object} userOrId
 * @returns {boolean}
 */
export const shouldShowDashboardStandinComplete = (search, userOrId) => {
  if (!hasJustCompletedIdentityStandinQuery(search)) {
    return false;
  }
  const userId = userIdOf(userOrId);
  if (userId && shownStandinCompleteThisLoad.has(userId)) {
    return true;
  }
  if (hasSeenIdentityStandinComplete(userOrId)) {
    return false;
  }
  return true;
};

const hasJustCompletedIdentityStandinQuery = search => {
  if (!search || typeof search !== 'string') {
    return false;
  }
  const query = search.startsWith('?') ? search.slice(1) : search;
  return query.split('&').some(part => {
    const [key, value] = part.split('=');
    return key === 'standin' && value === 'done';
  });
};

/**
 * Drop the in-memory “shown this load” flag (new visit). Keeps localStorage.
 *
 * @param {string|Object} [userOrId]
 */
export const clearIdentityStandinCompleteShownThisLoad = userOrId => {
  const userId = userIdOf(userOrId);
  if (userId) {
    shownStandinCompleteThisLoad.delete(userId);
    return;
  }
  shownStandinCompleteThisLoad.clear();
};

/**
 * Test helper: clear memory and localStorage for one user or everyone.
 *
 * @param {string|Object} [userOrId]
 */
export const resetIdentityStandinCompleteSeen = userOrId => {
  const userId = userIdOf(userOrId);
  if (userId) {
    shownStandinCompleteThisLoad.delete(userId);
    if (canUseStorage()) {
      try {
        window.localStorage.removeItem(identityStandinCompleteSeenKey(userId));
      } catch (e) {
        // ignore
      }
    }
    return;
  }
  shownStandinCompleteThisLoad.clear();
};

export const identityFromUser = user => {
  const profile = profileOf(user);
  const publicData = profile.publicData || {};
  const protectedData = profile.protectedData || {};
  const ageVerified = !!(publicData.ageVerified || protectedData.ageVerified);
  const identityVerified = !!(publicData.identityVerified || protectedData.identityVerified);
  const verifiedAt = publicData.verifiedAt || protectedData.verifiedAt || null;
  return {
    ageVerified,
    identityVerified,
    verifiedAt,
    isVerified: ageVerified && identityVerified,
  };
};

export const isUserIdentityVerified = user => identityFromUser(user).isVerified;

export const isListingAuthorVerified = listing => isUserIdentityVerified(listing?.author);

export const bothPartiesIdentityVerified = (userA, userB) =>
  isUserIdentityVerified(userA) && isUserIdentityVerified(userB);

export const otherPartyFromTransaction = (transaction, currentUser) => {
  const currentId = currentUser?.id?.uuid;
  if (!currentId || !transaction) {
    return null;
  }
  const customer = transaction.customer;
  const provider = transaction.provider;
  if (customer?.id?.uuid === currentId) {
    return provider || null;
  }
  if (provider?.id?.uuid === currentId) {
    return customer || null;
  }
  return provider || customer || null;
};

export const isIdentityRequiredError = error =>
  error?.code === IDENTITY_REQUIRED_CODE ||
  error?.data?.code === IDENTITY_REQUIRED_CODE ||
  error?.statusText === IDENTITY_REQUIRED_CODE;

export const identityRequiredError = (message = 'Identity verification required') => {
  const error = new Error(message);
  error.status = 403;
  error.code = IDENTITY_REQUIRED_CODE;
  error.statusText = IDENTITY_REQUIRED_CODE;
  return error;
};
